<script setup lang="ts">
  import { RouterLink } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { Routes } from '@/const/Routes'
  import usePendo from '@/composables/usePendo'
  import { useAuthStore } from '@/stores/auth'

  import { KsButton, KsIcon } from '@aschehoug/kloss'
  import Typography from '@/components/common/Typography.vue'

  const { t } = useI18n()
  const { showPendoGuide } = usePendo()
  const { isAuthenticated } = useAuthStore()

  interface IAppFooter {
    darkmode?:boolean,
  }
  defineProps<IAppFooter>()
</script>

<template>
  <footer :class="{'darkmode' : darkmode}">
    <nav>
      <ul>
        <li>
          <KsButton
            href="https://skole.aschehoug.no/kundeservice"
            target="_blank"
            variant="tertiary"
          >
            <KsIcon id="headphones" />
            {{ t('footer.service') }}
          </KsButton>
        </li>
        <li>
          <router-link
            :to="{ name: Routes.Privacy.name }"
            class="link"
          >
            <KsButton
              type="label"
              variant="tertiary"
            >
              <KsIcon id="lock" />
              {{ t('footer.privacy') }}
            </KsButton>
          </router-link>
        </li>
        <li>
          <router-link
            :to="{ name: Routes.Accessibility.name }"
            class="link"
          >
            <KsButton
              type="label"
              variant="tertiary"
            >
              <KsIcon id="universal-access" />
              {{ t('footer.uu') }}
            </KsButton>
          </router-link>
        </li>
        <li>
          <KsButton
            href="https://skole.aschehoug.no/"
            target="_blank"
            variant="tertiary"
          >
            <KsIcon id="bag" />
            {{ t('footer.store') }}
          </KsButton>
        </li>
        <li>
          <router-link
            :to="{ name: Routes.Rights.name }"
            class="link"
          >
            <KsButton
              type="label"
              variant="tertiary"
            >
              <KsIcon id="copyright" />
              {{ t('footer.copyrights') }}
            </KsButton>
          </router-link>
        </li>
      </ul>
    </nav>

    <Typography class="trademark">
      {{ `Aschehoug &copy; ${new Date().getFullYear()}` }}
    </Typography>
  </footer>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  footer {
    --ks-secondary: var(--color-pair-background-color);

    color: var(--color-pair-background-color, var(--app-theme-color));
    background-color: var(--color-pair-color, var(--app-theme-background-color));
    text-align: center;
    padding: 40px auto;
    width: 100%;

    @include vgs-media-tablet-and-desktop {
      padding: 96px 0 40px;
    }

    :deep(.ks-button) {
      font-size: 1.125rem;

      @include vgs-media-mobile {
        padding-left: 0;
      }
    }

    .link {
      // Safari specific color + text-decoration
      color: inherit;
      text-decoration: none;

      &:focus-visible {
        box-shadow: none;

        .ks-button {
          @include focus-ring;
        }
      }
    }

    ul {
      list-style: none;
      display: flex;
      gap: 12px;
      align-items: center;
      justify-content: center;
      flex-flow: row wrap;
      padding: 24px;
      margin: 0 0 8px;

      @include vgs-media-mobile {
        flex-direction: column;
        align-items: flex-start;
      }

      li {
        font-size: 1.125rem; // 18px
        font-style: normal;
        font-weight: 500;
        line-height: 17px;
        display: flex;
        align-items: center;
        gap: 7px;
      }
    }

    .trademark {
      margin: 0;
    }

    .darkmode {
      background: var(--color-blue-50);
      color: var(--color-white);

      li {
        color: var(--color-white);
      }
    }
  }
</style>
